/**
 * Chat Tab — main chat interface with FishMemory bot.
 * Uses inverted FlatList for stable scroll: newest messages at bottom,
 * older messages load automatically when scrolling up.
 */

import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  Text,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  NativeSyntheticEvent,
  NativeScrollEvent,
  Modal,
  Pressable,
  Image,
} from 'react-native';
import * as Clipboard from 'expo-clipboard';
import * as Haptics from 'expo-haptics';
import { BlurView } from 'expo-blur';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import { Ionicons } from '@expo/vector-icons';
import { useChatStore } from '../../src/stores/chatStore';
import { useAuthStore } from '../../src/stores/authStore';
import { useToastStore } from '../../src/stores/toastStore';
import { useNavigationStore } from '../../src/stores/navigationStore';
import MessageBubble from '../../src/components/chat/MessageBubble';
import ChatInput from '../../src/components/chat/ChatInput';
import TypingIndicator from '../../src/components/chat/TypingIndicator';
import EmptyState from '../../src/components/common/EmptyState';
import SkeletonLoader from '../../src/components/common/SkeletonLoader';
import { useTheme, type ThemeColors } from '../../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS, SHADOW } from '../../src/utils/design-tokens';
import { hasSeenFirstReminderPrompt, markFirstReminderPromptShown } from '../../src/utils/onboarding';
import { useBackHandler } from '../../src/navigation/useBackHandler';
import type { ChatMessage } from '../../src/types/api';

const PAGE_SIZE = 20;
const SCROLL_BTN_THRESHOLD = 300;

export default function ChatScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const flatListRef = useRef<FlatList>(null);
  const user = useAuthStore((s) => s.user);
  const { colors, isDark } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);
  const tabBarBottomSpace = useNavigationStore((state) => state.tabBarBottomSpace);
  const tabBarKeyboardVisible = useNavigationStore((state) => state.tabBarKeyboardVisible);
  const reservedBottomSpace = tabBarBottomSpace > 0 ? tabBarBottomSpace : 46 + Math.max(insets.bottom, 6);
  const bottomChromeSpace = tabBarKeyboardVisible ? 0 : reservedBottomSpace;

  const messages = useChatStore((s) => s.messages);
  const wsConnected = useChatStore((s) => s.wsConnected);
  const wsState = useChatStore((s) => s.wsState);
  const isBotTyping = useChatStore((s) => s.isBotTyping);
  const isLoadingMore = useChatStore((s) => s.isLoadingMore);
  const hasMore = useChatStore((s) => s.hasMore);
  const sendMessage = useChatStore((s) => s.sendMessage);
  const sendButton = useChatStore((s) => s.sendButton);
  const sendListSelect = useChatStore((s) => s.sendListSelect);
  const clearMessages = useChatStore((s) => s.clearMessages);
  const loadMoreHistory = useChatStore((s) => s.loadMoreHistory);
  const deleteMessage = useChatStore((s) => s.deleteMessage);
  const toggleReaction = useChatStore((s) => s.toggleReaction);

  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFirstPrompt, setShowFirstPrompt] = useState(false);
  
  const isNearBottom = useRef(true);
  const prevMsgCount = useRef(messages.length);

  // Show first reminder prompt if user hasn't seen it
  useEffect(() => {
    hasSeenFirstReminderPrompt().then((seen) => {
      if (!seen) setShowFirstPrompt(true);
    });
  }, []);

  // Keep the current reading position when a live message arrives while the user
  // is browsing older chat. The inverted list handles the inserted row without
  // moving visible content; only follow new messages when already near latest.
  useEffect(() => {
    const diff = messages.length - prevMsgCount.current;
    prevMsgCount.current = messages.length;
    if (diff <= 0) return;

    if (isNearBottom.current) {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
      setUnreadCount(0);
      return;
    }

    setUnreadCount((count) => count + diff);
    setShowScrollBtn(true);
  }, [messages.length]);

  // Data: already newest first (index 0 = bottom of screen) because of store inversion
  // Insert timestamp separators between messages >5min apart
  const invertedData = useMemo(() => {
    const result: (ChatMessage | { _separator: true; timestamp: string })[] = [];
    for (let i = 0; i < messages.length; i++) {
      result.push(messages[i]);
      if (i < messages.length - 1) {
        const curr = new Date(messages[i].created_at_utc).getTime();
        const next = new Date(messages[i + 1].created_at_utc).getTime();
        if (Math.abs(curr - next) > 5 * 60 * 1000) {
          const d = new Date(messages[i + 1].created_at_utc);
          const now = new Date();
          const isToday = d.toDateString() === now.toDateString();
          const yesterday = new Date(now);
          yesterday.setDate(yesterday.getDate() - 1);
          const isYesterday = d.toDateString() === yesterday.toDateString();
          let label: string;
          if (isToday) {
            label = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          } else if (isYesterday) {
            label = `Yesterday ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
          } else {
            label = d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          }
          result.push({ _separator: true, timestamp: label });
        }
      }
    }
    return result;
  }, [messages]);

  // Debounce to stop rapid re-fires that cause spinner flicker
  const endReachedCooldown = useRef(false);
  const handleEndReached = useCallback(() => {
    if (endReachedCooldown.current || isLoadingMore || !hasMore) return;
    endReachedCooldown.current = true;
    loadMoreHistory().finally(() => {
      setTimeout(() => { endReachedCooldown.current = false; }, 500);
    });
  }, [isLoadingMore, hasMore, loadMoreHistory]);

  // Track scroll position for scroll-to-bottom FAB + near-bottom detection
  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const offsetY = e.nativeEvent.contentOffset.y;
      const nearBottom = offsetY < 100;
      isNearBottom.current = nearBottom;
      setShowScrollBtn(offsetY > SCROLL_BTN_THRESHOLD);
      if (nearBottom) setUnreadCount(0);
    },
    [],
  );

  const scrollToBottom = useCallback(() => {
    flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
    setUnreadCount(0);
  }, []);

  const handleButtonPress = useCallback(
    (id: string, label?: string) => {
      sendButton(id, label);
    },
    [sendButton],
  );

  const handleListSelect = useCallback(
    (id: string, label?: string) => {
      sendListSelect(id, label);
    },
    [sendListSelect],
  );

  const toggleSelection = useCallback((msgId: string | number) => {
    setSelectedMessageIds((prev) => {
      const idStr = String(msgId);
      if (prev.includes(idStr)) {
        return prev.filter((id) => id !== idStr);
      } else {
        return [...prev, idStr];
      }
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMessageIds([]);
  }, []);

  useBackHandler({
    id: 'chat-tab',
    label: 'Chat',
    priority: 700,
    enabled: showDeleteModal || selectedMessageIds.length > 0 || showFirstPrompt,
    onBack: () => {
      if (showDeleteModal) {
        setShowDeleteModal(false);
        return true;
      }
      if (selectedMessageIds.length > 0) {
        clearSelection();
        return true;
      }
      if (showFirstPrompt) {
        markFirstReminderPromptShown();
        setShowFirstPrompt(false);
        return true;
      }
      return false;
    },
  });

  const handleBulkCopy = async () => {
    const selectedMsgs = messages.filter(m => selectedMessageIds.includes(String(m.id)));
    const sorted = [...selectedMsgs].sort((a, b) => new Date(a.created_at_utc).getTime() - new Date(b.created_at_utc).getTime());
    
    const textToCopy = sorted.map(m => {
      if (!m.body) return null;
      
      const d = new Date(m.created_at_utc);
      const mm = (d.getMonth() + 1).toString().padStart(2, '0');
      const dd = d.getDate().toString().padStart(2, '0');
      
      let h = d.getHours();
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      h = h ? h : 12; // the hour '0' should be '12'
      const hh = h.toString().padStart(2, '0');
      const min = d.getMinutes().toString().padStart(2, '0');
      
      const sender = m.direction === 'inbound' ? 'Me' : 'FishMemory';
      return `[${mm}/${dd}, ${hh}:${min} ${ampm}] ${sender}: ${m.body}`;
    }).filter(Boolean).join('\n');

    if (textToCopy) {
      await Clipboard.setStringAsync(textToCopy);
      useToastStore.getState().showToast('Copied', `${selectedMessageIds.length} messages copied to clipboard`);
    }
    clearSelection();
  };

  const confirmBulkDelete = () => {
    setShowDeleteModal(true);
  };

  const handleReact = useCallback((messageId: number, emoji: string) => {
    toggleReaction(messageId, emoji);
  }, [toggleReaction]);

  const handleBulkDelete = () => {
    Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    selectedMessageIds.forEach(id => {
      deleteMessage(id);
    });
    setShowDeleteModal(false);
    clearSelection();
  };

  const renderMessage = useCallback(
    ({ item, index }: { item: ChatMessage | { _separator: true; timestamp: string }; index: number }) => {
      if ('_separator' in item) {
        return (
          <View style={s.timestampSeparator}>
            <Text style={s.timestampText}>{item.timestamp}</Text>
          </View>
        );
      }

      // Message grouping: group consecutive messages from same sender
      // In inverted list, index+1 = visually above (older), index-1 = visually below (newer)
      const prevItem = invertedData[index + 1]; // visually above
      const nextItem = invertedData[index - 1]; // visually below
      const prevIsMessage = prevItem && !('_separator' in prevItem);
      const nextIsMessage = nextItem && !('_separator' in nextItem);
      const prevSameDirection = prevIsMessage && (prevItem as ChatMessage).direction === item.direction;
      const nextSameDirection = nextIsMessage && (nextItem as ChatMessage).direction === item.direction;
      const isFirstInGroup = !prevSameDirection;
      const isLastInGroup = !nextSameDirection;

      const isSelected = selectedMessageIds.includes(String(item.id));
      return (
        <MessageBubble
          message={item}
          onButtonPress={handleButtonPress}
          onListSelect={handleListSelect}
          isSelected={isSelected}
          selectionMode={selectedMessageIds.length > 0}
          onToggleSelection={toggleSelection}
          onReact={handleReact}
          onRetry={item.status === 'failed' ? () => {
            sendMessage(item.body || '');
          } : undefined}
          isFirstInGroup={isFirstInGroup}
          isLastInGroup={isLastInGroup}
        />
      );
    },
    [handleButtonPress, handleListSelect, selectedMessageIds, toggleSelection, sendMessage, invertedData, handleReact],
  );

  const handleSend = useCallback((text: string) => {
    sendMessage(text);
    setTimeout(() => {
      flatListRef.current?.scrollToOffset({ offset: 0, animated: true });
      setUnreadCount(0);
    }, 50);
  }, [sendMessage]);

  const keyExtractor = useCallback(
    (item: ChatMessage | { _separator: true; timestamp: string }) => {
      if ('_separator' in item) return `sep-${item.timestamp}`;
      return `${item.id}-${item.clientMsgId || ''}`;
    },
    [],
  );



  const listFooter = useMemo(() => {
    if (!hasMore || invertedData.length === 0) return null;
    return (
      <View style={[s.loadMoreBtn, !isLoadingMore && { opacity: 0 }]}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }, [hasMore, invertedData.length, isLoadingMore]);

  const confirmClearChat = useCallback(() => {
    if (Platform.OS === 'web') {
      if (window.confirm(t('chat.clearConfirm'))) clearMessages();
      return;
    }
    Alert.alert(
      t('chat.clearTitle'),
      t('chat.clearConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('chat.clear'), style: 'destructive', onPress: clearMessages },
      ],
    );
  }, [clearMessages, t]);

  return (
    <View style={[s.container, { paddingTop: insets.top, paddingBottom: bottomChromeSpace }]}>
      {/* Absolute Glass Header */}
      <View style={[s.headerContainer, { top: insets.top }]}>
        {selectedMessageIds.length > 0 ? (
          <BlurView intensity={80} tint={isDark ? 'dark' : 'light'} style={[s.header, s.selectionHeader]}>
            <TouchableOpacity onPress={clearSelection} style={s.headerIconWrap} accessibilityLabel="Cancel selection">
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
            <View style={s.headerInfo}>
              <Text style={s.selectionTitle}>{selectedMessageIds.length} Selected</Text>
            </View>
            <TouchableOpacity onPress={handleBulkCopy} style={s.actionsButton} accessibilityRole="button" accessibilityLabel="Copy selected" accessibilityHint="Copies selected messages to clipboard">
              <Ionicons name="copy-outline" size={20} color={colors.text} />
            </TouchableOpacity>
            <TouchableOpacity onPress={confirmBulkDelete} style={[s.actionsButton, { marginLeft: 8 }]} accessibilityRole="button" accessibilityLabel="Delete selected" accessibilityHint="Deletes selected messages permanently">
              <Ionicons name="trash-outline" size={20} color={colors.error} />
            </TouchableOpacity>
          </BlurView>
        ) : (
          <BlurView intensity={80} tint={isDark ? 'dark' : 'light'} style={s.header}>
            <View style={s.headerIconWrap}>
              <Image source={require('../../assets/logo.png')} style={{ width: 28, height: 28, resizeMode: 'contain' }} />
            </View>
            <View style={s.headerInfo}>
              <Text style={s.headerTitle}>FishMemory</Text>
              <TouchableOpacity
                style={s.statusDotRow}
                activeOpacity={0.7}
                disabled={wsState === 'connected'}
                onPress={() => useChatStore.getState().initWebSocket()}
                accessibilityRole={wsState === 'connected' ? 'text' : 'button'}
                accessibilityLabel={wsState === 'connected'
                  ? t('chat.online')
                  : t('chat.reconnecting', 'Reconnecting...')}
              >
                <View style={[s.statusDot, wsState === 'connected' && s.statusDotOnline, wsState === 'disconnected' && s.statusDotOffline]} />
                <Text style={s.headerSubtitle}>
                  {wsState === 'connected' ? t('chat.online') : wsState === 'reconnecting' ? t('chat.reconnecting', 'Reconnecting...') : wsState === 'connecting' ? t('chat.connecting') : t('chat.offline', 'Offline')}
                </Text>
              </TouchableOpacity>
            </View>
            {messages.length > 0 && (
              <TouchableOpacity
                style={s.actionsButton}
                onPress={confirmClearChat}
                accessibilityRole="button"
                accessibilityLabel={t('chat.clear')}
              >
                <Ionicons name="trash-outline" size={16} color="#94A3B8" />
              </TouchableOpacity>
            )}
          </BlurView>
        )}
      </View>

      <KeyboardAvoidingView
        style={s.chatArea}
        behavior={Platform.OS === 'web' ? undefined : 'padding'}
        keyboardVerticalOffset={0}
      >
        {/* Messages — inverted list: newest at bottom, scroll up for older */}
        {invertedData.length === 0 ? (
          <View style={s.emptyContainer}>
            {!wsConnected ? (
              <SkeletonLoader variant="chat" rows={6} />
            ) : (
              <>
              <EmptyState
                emoji="💬"
                title={t('chat.empty')}
                subtitle={t('chat.emptyHint')}
                actionLabel={t('chat.emptyAction')}
                onAction={() => {
                  sendMessage(t('onboarding.firstReminderExample'));
                  if (showFirstPrompt) {
                    markFirstReminderPromptShown();
                    setShowFirstPrompt(false);
                  }
                }}
              />
              {showFirstPrompt && (
                <View style={s.firstPromptCard}>
                  <Text style={s.firstPromptTitle}>{t('onboarding.firstReminderTitle')}</Text>
                  <Text style={s.firstPromptHint}>{t('onboarding.firstReminderHint')}</Text>
                  <View style={s.firstPromptExample}>
                    <Text style={s.firstPromptExampleText}>"{t('onboarding.firstReminderExample')}"</Text>
                  </View>
                  <TouchableOpacity
                    style={s.firstPromptDismiss}
                    onPress={() => { markFirstReminderPromptShown(); setShowFirstPrompt(false); }}
                    accessibilityRole="button"
                  >
                    <Text style={s.firstPromptDismissText}>{t('onboarding.gotIt')}</Text>
                  </TouchableOpacity>
                </View>
              )}
              </>
            )}
          </View>
        ) : (
        <FlatList
          ref={flatListRef}
          data={invertedData}
          renderItem={renderMessage}
          keyExtractor={keyExtractor}
          style={s.messageList}
          contentContainerStyle={s.messageContent}
          inverted={true}
          maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
          windowSize={7}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={50}
          removeClippedSubviews={Platform.OS === 'android'}
          initialNumToRender={PAGE_SIZE}
          showsVerticalScrollIndicator={true}
          persistentScrollbar={true}
          scrollEventThrottle={16}
          onScroll={handleScroll}
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.5}
          ListHeaderComponent={<TypingIndicator visible={isBotTyping} />}
          ListFooterComponent={listFooter}
        />
        )}

        {/* Scroll to bottom FAB + unread badge */}
        {showScrollBtn && (
          <TouchableOpacity
            style={s.scrollBtn}
            onPress={scrollToBottom}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={t('chat.scrollToLatest', 'Scroll to latest')}
          >
            <Ionicons name="arrow-down" size={20} color="#1565C0" />
            {unreadCount > 0 && (
              <View style={s.unreadBadge}>
                <Text style={s.unreadBadgeText}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        )}

        {/* Input */}
        <ChatInput onSend={handleSend} />
      </KeyboardAvoidingView>

      {/* Delete Confirmation Modal */}
      <Modal visible={showDeleteModal} transparent animationType="fade">
        <BlurView intensity={Platform.OS === 'ios' ? 30 : 0} tint="dark" style={s.modalOverlay}>
          <View style={s.modalContent}>
            <Text style={s.modalTitle}>{t('chat.deleteMessages', 'Delete Messages')}</Text>
            <Text style={s.modalText}>
              {t('chat.confirmBulkDelete', { count: selectedMessageIds.length, defaultValue: `Are you sure you want to delete ${selectedMessageIds.length} message${selectedMessageIds.length > 1 ? 's' : ''}?` })}
            </Text>
            <View style={s.modalButtons}>
              <TouchableOpacity style={[s.modalBtn, s.modalBtnCancel]} onPress={() => setShowDeleteModal(false)}>
                <Text style={s.modalBtnCancelText}>{t('common.cancel', 'Cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[s.modalBtn, s.modalBtnDelete]} onPress={handleBulkDelete}>
                <Text style={s.modalBtnDeleteText}>{t('common.delete', 'Delete')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </BlurView>
      </Modal>
    </View>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  headerContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.base,
    paddingVertical: SPACING.sm,
    backgroundColor: colors.surface + 'B3',
    gap: SPACING.md,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border,
  },
  headerIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceTinted,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_500Medium',
    color: colors.text,
    letterSpacing: 0.3,
  },
  statusDotRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.warning,
  },
  statusDotOnline: {
    backgroundColor: colors.success,
  },
  statusDotOffline: {
    backgroundColor: colors.error,
  },
  headerSubtitle: {
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_400Regular',
    color: colors.textDim,
    letterSpacing: 0.2,
  },
  actionsButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.surfaceTinted,
  },
  chatArea: {
    flex: 1,
    paddingTop: 52, // Space for the absolute header
  },
  messageList: {
    flex: 1,
  },
  messageContent: {
    paddingVertical: 8,
  },
  loadMoreBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.lg,
    marginHorizontal: SPACING['3xl'],
    marginVertical: SPACING.sm,
    borderRadius: RADIUS.xl,
    backgroundColor: colors.surface,
    borderWidth: 0.5,
    borderColor: colors.border,
    gap: 6,
  },
  scrollBtn: {
    position: 'absolute',
    right: SPACING.base,
    bottom: 72,
    width: 40,
    height: 40,
    borderRadius: RADIUS.xl,
    backgroundColor: colors.surface,
    borderWidth: 0.5,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
    ...SHADOW.card,
  },
  unreadBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  unreadBadgeText: {
    color: '#fff',
    fontSize: FONT_SIZE.xs,
    fontFamily: 'Inter_700Bold',
  },
  scrollBtnText: {
    fontSize: 22,
    color: '#fff',
    fontWeight: '700',
    marginTop: -2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectionHeader: {
    backgroundColor: colors.surfaceTinted,
  },
  selectionTitle: {
    fontSize: FONT_SIZE.base,
    fontFamily: 'Inter_600SemiBold',
    color: colors.text,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '80%',
    backgroundColor: colors.background,
    borderRadius: RADIUS.lg,
    padding: SPACING.xl,
    alignItems: 'center',
    ...SHADOW.elevated,
  },
  modalTitle: {
    fontSize: FONT_SIZE.md,
    fontFamily: 'PlusJakartaSans_600SemiBold',
    color: colors.text,
    marginBottom: SPACING.sm,
  },
  modalText: {
    fontSize: FONT_SIZE.sm,
    fontFamily: 'Inter_400Regular',
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: SPACING.xl,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: SPACING.md,
    width: '100%',
  },
  modalBtn: {
    flex: 1,
    paddingVertical: SPACING.md,
    borderRadius: RADIUS.sm,
    alignItems: 'center',
  },
  modalBtnCancel: {
    backgroundColor: colors.primary + '1F',
  },
  modalBtnDelete: {
    backgroundColor: colors.error,
  },
  modalBtnCancelText: {
    color: colors.text,
    fontFamily: 'Inter_600SemiBold',
  },
  modalBtnDeleteText: {
    color: '#FFF',
    fontFamily: 'Inter_600SemiBold',
  },
  timestampSeparator: {
    alignItems: 'center',
    marginVertical: SPACING.md,
  },
  timestampText: {
    fontSize: FONT_SIZE.xs,
    color: colors.textSecondary,
    backgroundColor: colors.surface,
    paddingHorizontal: SPACING.md,
    paddingVertical: 3,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
  },
  firstPromptCard: {
    marginTop: SPACING.base,
    marginHorizontal: SPACING.xl,
    padding: SPACING.lg,
    backgroundColor: colors.surface,
    borderRadius: RADIUS.lg,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  firstPromptTitle: {
    fontFamily: 'PlusJakartaSans_600SemiBold',
    fontSize: FONT_SIZE.base,
    color: colors.text,
    marginBottom: 6,
  },
  firstPromptHint: {
    fontFamily: 'Inter_400Regular',
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
    marginBottom: SPACING.md,
  },
  firstPromptExample: {
    backgroundColor: colors.primary + '14',
    paddingVertical: SPACING.md,
    paddingHorizontal: SPACING.base,
    borderRadius: RADIUS.md,
    marginBottom: SPACING.md,
  },
  firstPromptExampleText: {
    fontFamily: 'Inter_500Medium',
    fontSize: FONT_SIZE.sm,
    color: colors.primary,
    fontStyle: 'italic',
  },
  firstPromptDismiss: {
    paddingVertical: 6,
    paddingHorizontal: SPACING.base,
  },
  firstPromptDismissText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: FONT_SIZE.sm,
    color: colors.textSecondary,
  },
});
